import dayjs from "dayjs";
import {
  EventSearchDetails,
  TflAutoCompleteResult,
} from "queries/ticket.queries";
import React from "react";
import { ActivityIndicator, View } from "react-native";

const AutoCompleteResult = ({
  autoCompleteFetching,
  autoCompleteResult,
  debouncedSearchQuery,
  onEventPress,
  onPerformerPress,
  onVenuePress,
}: {
  autoCompleteFetching: boolean;
  autoCompleteResult?: TflAutoCompleteResult;
  debouncedSearchQuery: string;
  onEventPress: (eventId: EventSearchDetails | undefined) => void;
  onPerformerPress: (performerId: string) => void;
  onVenuePress: (venueId: string) => void;
}) => {
  if (autoCompleteFetching) {
    return (
      <div className="bg-white px-4 py-2 flex items-center gap-2">
        <ActivityIndicator />
        <div>Loading...</div>
      </div>
    );
  }
  const result: React.ReactNode[] = [];

  if (!autoCompleteResult) {
    return <></>; // Need this in order to render the list
  }

  if (
    autoCompleteResult.events.length === 0 &&
    autoCompleteResult.performers.length === 0 &&
    autoCompleteResult.venues.length === 0
  ) {
    return (
      <div className="bg-white px-4 py-2 flex items-center gap-2">
        <div>
          Sorry, we could not find any results for{" "}
          <span className="font-bold">{debouncedSearchQuery}</span>
        </div>
      </div>
    );
  }

  if (autoCompleteResult.performers.length > 0) {
    result.push(
      <div key="performers" className="border-b border-gray-100">
        <div className="px-6 py-3 bg-gray-50">
          <div>
            🎤 Performers
            <span className="ml-2 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
              {autoCompleteResult.performers.length} results
            </span>
          </div>
        </div>
      </div>
    );
    autoCompleteResult.performers.forEach((item) => {
      result.push(
        <div
          key={item.id}
          className="px-6 py-4 hover:bg-green-50 cursor-pointer transition-all duration-200"
          onClick={() => onPerformerPress(item.name)}
        >
          <div className="flex items-center justify-between space-x-3">
            {item.thumbnail ? (
              <img className="w-12 h-12 rounded-lg" src={item.thumbnail} />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-300 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <div className="font-semibold text-gray-800 mb-1">
                {item.name}
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (autoCompleteResult.events.length > 0) {
    result.push(
      <div key="events" className="border-b border-gray-100">
        <div className="px-6 py-3 bg-gray-50">
          <div>
            🎪 Events
            <span className="ml-2 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">
              {autoCompleteResult.events.length} results
            </span>
          </div>
        </div>
      </div>
    );
    autoCompleteResult.events.forEach((item) => {
      result.push(
        <div
          key={item.eventId}
          className="px-6 py-4 hover:bg-purple-50 cursor-pointer transition-all duration-200"
          onClick={() => onEventPress(item)}
        >
          <div className="flex items-start">
            <div className="flex-1">
              <div className="font-semibold text-gray-800 mb-1">
                {item.title}
              </div>
              <div className="text-xs text-gray-500">
                <span className="flex items-center">
                  📅 {dayjs(item.localDateTime).format("YYYY-MM-DD hh:mm A")}
                </span>
                <span className="flex items-center">
                  📍 {item.venueName} - {item.venueCity}, {item.venueState}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (autoCompleteResult.venues.length > 0) {
    result.push(
      <div key="venues" className="border-b border-gray-100">
        <div className="px-6 py-3 bg-gray-50">
          <div>
            🏟️ Venues
            <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
              {autoCompleteResult.venues.length} results
            </span>
          </div>
        </div>
      </div>
    );
    autoCompleteResult.venues.forEach((item) => {
      result.push(
        <div
          key={item.id}
          className="px-6 py-4 hover:bg-blue-50 cursor-pointer transition-all duration-200"
          onClick={() => onVenuePress(item.name)}
        >
          <div className="flex items-center justify-between space-x-3">
            {item.thumbnail ? (
              <img className="w-12 h-12 rounded-lg" src={item.thumbnail} />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-300 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <div className="font-semibold text-gray-800 mb-1">
                {item.name}
              </div>
              <div className="flex items-center text-xs text-gray-500 space-x-4">
                <span className="flex items-center">
                  📍 {item.city}, {item.region}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <View className="bg-white shadow-xl border border-gray-100 overflow-hidden">
      {result}
    </View>
  );
};

export default AutoCompleteResult;
